import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { MathTextBlock } from '../components/MathTextBlock';
import {
  factoryOrderDisplayTitle,
  factoryService,
  type FactoryOrderListFilter,
  type FactoryReleasePreviewDraft,
} from '../services/factoryService';

type LocationState = {
  filter?: FactoryOrderListFilter;
  focusDraftId?: string;
  resumeAccept?: {
    orderId: string;
    batchId: string;
    pendingStart?: boolean;
  };
};

export function LearningFactoryOrderDetailPage() {
  const { orderId = '' } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state as LocationState | null) || {};
  const returnFilter = state.filter || 'done';
  const focusDraftId = state.focusDraftId || null;
  const resumeAccept = state.resumeAccept || null;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<FactoryReleasePreviewDraft[]>([]);
  const [orderTitle, setOrderTitle] = useState('');
  const [orderCode, setOrderCode] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!orderId) return;
      try {
        setLoading(true);
        setError(null);
        const preview = await factoryService.getOrderReleasePreview(orderId);
        if (cancelled) return;
        setDrafts(preview.products.flatMap((p) => p.bankDrafts));
        setOrderTitle(factoryOrderDisplayTitle(preview.order));
        setOrderCode(preview.order.orderCode || '');
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orderId]);

  useEffect(() => {
    if (!focusDraftId || loading) return;
    const timer = window.setTimeout(() => {
      const el = document.querySelector(`[data-factory-draft="${CSS.escape(focusDraftId)}"]`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 80);
    return () => window.clearTimeout(timer);
  }, [focusDraftId, loading, drafts]);

  const particleCount = drafts.length;

  function goBackToOrders() {
    navigate('/factory-orders', {
      state: {
        filter: returnFilter,
        ...(resumeAccept ? { resumeAccept } : {}),
      } satisfies LocationState,
    });
  }

  return (
    <div className="mx-auto max-w-3xl space-y-5 px-4 py-6">
      <header className="space-y-2">
        <button
          type="button"
          onClick={goBackToOrders}
          className="text-sm font-medium text-primary"
        >
          ← 返回订单
        </button>
        <h1 className="text-xl font-semibold text-ink">释放预览</h1>
        <p className="text-sm text-muted">
          {orderTitle}
          {orderCode ? ` · 订单码 ${orderCode}` : ''}
          {particleCount ? ` · ${particleCount} 个将释放颗粒` : ''}
        </p>
        <p className="text-sm text-muted">
          以下诱导题目来自成品智能体，将作为知识颗粒参与复习。点击条目查看知识颗粒详情。
        </p>
        {resumeAccept ? (
          <p className="rounded-lg border border-amber-200 bg-amber-50/80 px-3 py-2 text-xs text-amber-900">
            查看完成后请返回订单列表，继续确认验收
            {resumeAccept.pendingStart ? '并开始释放' : ''}。
          </p>
        ) : null}
      </header>

      {error ? (
        <p className="rounded-xl border border-[#F2C6CC] bg-[#FFF3F3] px-4 py-3 text-sm text-danger">{error}</p>
      ) : null}
      {loading ? <p className="text-sm text-muted">加载中…</p> : null}
      {!loading && drafts.length === 0 ? (
        <p className="text-sm text-muted">暂无将要释放的知识颗粒。</p>
      ) : null}

      <ul className="space-y-2">
        {drafts.map((draft, index) => (
          <li
            key={draft.id}
            data-factory-draft={draft.id}
            className={`rounded-xl border bg-canvas/30 ${
              focusDraftId === draft.id ? 'border-primary' : 'border-border'
            }`}
          >
            <button
              type="button"
              onClick={() => navigate(`/factory-orders/${orderId}/particles/${encodeURIComponent(draft.id)}`, {
                state: { filter: returnFilter },
              })}
              className="flex w-full items-start gap-3 px-3 py-3 text-left transition hover:bg-black/[0.02]"
            >
              <span className="w-6 shrink-0 text-xs font-bold tabular-nums text-muted">{index + 1}</span>
              <div className="min-w-0 flex-1 text-sm font-medium text-ink">
                <MathTextBlock text={draft.title || '知识颗粒'} className="text-sm font-medium" />
              </div>
              <span className="shrink-0 text-xs font-semibold text-primary">详情</span>
            </button>
          </li>
        ))}
      </ul>

      <p className="text-xs text-muted">
        <button type="button" onClick={goBackToOrders} className="text-primary hover:underline">
          返回订单列表
        </button>
      </p>
    </div>
  );
}
